import { ReactNode } from 'react';
import { Chip, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';

// ==============================|| CUSTOM TABLE - TYPE-DRIVEN CELL RENDERING ||============================== //
// A server column config (see column-config.ts) describes each column's semantic `type`
// (`text` | `date` | `number` | `badge` | …). Pages render the visually-rich columns
// (identity + avatar, status color, action buttons) with their own renderers and let
// everything else fall through to `renderTypedCell`, so a newly-enabled BE column shows
// up correctly without any per-key page code.

const MutedDash = (
	<Typography variant="body2" sx={{ color: 'text.secondary' }}>
		—
	</Typography>
);

const asText = (value: unknown): string | null => {
	if (value == null) return null;
	if (typeof value === 'string') return value.trim() || null;
	if (typeof value === 'number') return String(value);
	// Listă de referințe/valori (ex. roles: [{ id, name, label }]) → etichetele unite
	// prin virgulă.
	if (Array.isArray(value)) {
		const parts = value
			.map((item) => asText(item))
			.filter((text): text is string => Boolean(text));
		return parts.length ? parts.join(', ') : null;
	}
	// Referință de nomenclator (ex. rol { id, name, label }): `label` e eticheta de
	// AFIȘARE (localizată, editabilă din nomenclator), `name` doar identificatorul
	// intern (ex. „Administration Admin") — afișăm label-ul, cu fallback pe name
	// (aceeași convenție ca helperul roleLabel).
	if (typeof value === 'object') {
		const { label, name } = value as { label?: unknown; name?: unknown };
		if (typeof label === 'string' && label.trim()) return label;
		return typeof name === 'string' ? name : null;
	}
	return null;
};

const dateCell = (value: unknown): ReactNode => {
	if (typeof value !== 'string' || !dayjs(value).isValid()) return MutedDash;
	return (
		<Tooltip title={dayjs(value).format('D MMMM YYYY, HH:mm')}>
			<Typography variant="body2" sx={{ width: 'fit-content' }}>
				{dayjs(value).format('DD MMM YYYY')}
			</Typography>
		</Tooltip>
	);
};

const badgeCell = (value: unknown): ReactNode => {
	// Boolean → „Da" (afirmativ, verde) / „Nu"; altfel eticheta text a valorii.
	if (typeof value === 'boolean') {
		return (
			<Chip
				label={value ? 'Da' : 'Nu'}
				size="small"
				variant="light"
				color={value ? 'success' : 'default'}
			/>
		);
	}
	const text = asText(value);
	return text ? (
		<Chip label={text} size="small" variant="light" color="default" />
	) : (
		MutedDash
	);
};

/**
 * Renders a cell generically from its config `type` and the raw row value. Used for
 * columns a page does not render itself; unknown types fall back to plain text.
 */
export const renderTypedCell = (type: string | undefined, value: unknown): ReactNode => {
	switch (type) {
		case 'date':
			return dateCell(value);
		case 'badge':
			return badgeCell(value);
		case 'number': {
			const num = typeof value === 'number' ? value : Number(value);
			return Number.isFinite(num) ? (
				<Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
					{num.toLocaleString('ro-RO')}
				</Typography>
			) : (
				MutedDash
			);
		}
		default: {
			// `text` și orice tip necunoscut → text simplu (o linie); obiecte nerandabile → „—".
			const text = asText(value);
			return text ? (
				<Typography variant="body2" noWrap>
					{text}
				</Typography>
			) : (
				MutedDash
			);
		}
	}
};
